;(function () {
  class ProgressiveTextAnimationComponent extends HTMLElement {
    constructor() {
      super()

      this.words = []
      this.images = []
      this.logoTextBlocks = []
      this.centerBlocks = []
      this.timeline = []
      this.totalUnits = 0
      this.animatedElements = new Set()

      this.progressBar = this.querySelector(
        '.progressive-text-animation__progress-bar'
      )

      this.scrollHandler = null
      this.resizeHandler = null
      this.wrapper = this.closest('.shopify-section')

      // 两个阶段的进度
      this.revealProgress = 0
      this.highlightProgress = 0

      // 完成标志
      this.revealCompleted = false  // Reveal clip-path 动画只执行一次
      this.highlightCompleted = false  // Highlight 完成后锁定状态，但向上滚动时可以反向

      // RAF 优化
      this.rafId = null
      this.isScrolling = false
      this.cachedRect = null
      this.cachedViewportHeight = null
      this.lastScrollTop = 0

      this.init()
    }

    init() {
      this.setupElements()
      this.bindEvents()
      this.handleScroll()
    }

    getHighlightDelayVh() {
      const raw = parseFloat(this.dataset.highlightDelayVh || '')
      return Number.isFinite(raw) ? raw : 15
    }

    getHighlightScrollMultiplier() {
      const raw = parseFloat(this.dataset.highlightScrollMultiplier || '')
      if (!Number.isFinite(raw)) return 3.2
      return Math.max(0.5, Math.min(10, raw))
    }

    setupElements() {
      this.words = []
      this.images = []
      this.logoTextBlocks = []
      this.centerBlocks = []
      this.timeline = []
      this.animatedElements.clear()
      this.totalUnits = 0

      // 重置完成标志
      this.revealCompleted = false
      this.highlightCompleted = false

      this.stage = this.querySelector('[data-stage]')
      this.content = this.querySelector('.progressive-text-animation__content')
      this.previousSection = this.findPreviousSection()

      // 只选择当前可见的内容块
      // 检查是移动端还是桌面端，过滤掉被隐藏的内容
      const isMobile = window.innerWidth < 990
      const allBlocks = Array.from(this.querySelectorAll('[data-block-type]'))
      const blocks = allBlocks.filter((block) => {
        const container = block.closest('.progressive-text-animation__content')
        if (!container) return true
        
        // 移动端：排除有 small-hide 或 medium-up-hide 的容器
        if (isMobile) {
          return !container.classList.contains('small-hide') && 
                 !container.classList.contains('medium-up-hide')
        }
        // 桌面端：排除有 large-up-hide 的容器
        else {
          return !container.classList.contains('large-up-hide')
        }
      })

      blocks.forEach((block) => {
        const type = block.dataset.blockType

        switch (type) {
          case 'text': {
            const entry = this.buildTextEntry(block)
            if (entry) {
              this.timeline.push(entry)
              this.totalUnits += entry.unitCount
            }
            break
          }
          case 'image': {
            const entry = this.buildImageEntry(block)
            if (entry) {
              this.timeline.push(entry)
              this.totalUnits += entry.unitCount
            }
            break
          }
          case 'logo-text': {
            const entry = this.buildLogoEntry(block)
            if (entry) {
              this.timeline.push(entry)
              this.totalUnits += entry.unitCount
            }
            break
          }
          case 'center': {
            const entry = this.buildCenterEntry(block)
            if (entry) {
              this.timeline.push(entry)
              this.totalUnits += entry.unitCount
            }
            break
          }
          default: {
            this.timeline.push({
              type: type || 'spacer',
              element: block,
              unitCount: 0,
            })
            break
          }
        }
      })

      if (this.totalUnits === 0) {
        this.totalUnits = 1
      }

      this.resetAnimations()
      this.updateTrailingHeight()
    }

    buildTextEntry(block) {
      const textElement = block.querySelector(
        '.progressive-text-animation__text'
      )
      if (!textElement) {
        return null
      }

      const text = (
        textElement.dataset.textContent ||
        textElement.textContent ||
        ''
      ).trim()
      const words = text.length
        ? text.split(/\s+/).filter((word) => word.length > 0)
        : []

      textElement.innerHTML = ''

      const wordElements = words.map((word) => {
        const wordElement = document.createElement('span')
        wordElement.className = 'progressive-text-animation__word'
        wordElement.textContent = word
        textElement.appendChild(wordElement)
        this.words.push(wordElement)
        return wordElement
      })

      return {
        type: 'text',
        block,
        words: wordElements,
        unitCount: wordElements.length,
      }
    }

    buildImageEntry(block) {
      const imageElement = block.querySelector(
        '.progressive-text-animation__image'
      )
      if (!imageElement) {
        return null
      }
      this.images.push(imageElement)

      return {
        type: 'image',
        block,
        element: imageElement,
        unitCount: 1,
      }
    }

    buildLogoEntry(block) {
      const logo = block.querySelector('.progressive-text-animation__logo')
      const textElement = block.querySelector(
        '.progressive-text-animation__logo-text'
      )

      const entry = {
        type: 'logo',
        block,
        logo,
        textElement,
        words: [],
        unitCount: 1,
      }

      if (textElement) {
        const text = (
          textElement.dataset.textContent ||
          textElement.textContent ||
          ''
        ).trim()
        const words = text.length
          ? text.split(/\s+/).filter((word) => word.length > 0)
          : []

        textElement.innerHTML = ''

        entry.words = words.map((word) => {
          const wordElement = document.createElement('span')
          wordElement.className = 'progressive-text-animation__word'
          wordElement.textContent = word
          textElement.appendChild(wordElement)
          this.words.push(wordElement)
          return wordElement
        })
      }

      this.logoTextBlocks.push(entry)
      return entry
    }

    buildCenterEntry(block) {
      const imageElement = block.querySelector(
        '.progressive-text-animation__center-image'
      )
      const lineElements =
        Array.from(
          block.querySelectorAll('.progressive-text-animation__center-line')
        ) || []

      const entry = {
        type: 'center',
        block,
        image: imageElement,
        lines: lineElements,
        unitCount: 1,
      }

      if (imageElement) {
        this.images.push(imageElement)
      }
      this.centerBlocks.push(entry)
      return entry
    }

    toggleCenterBlockAnimation(entry, shouldAnimate, isCompleted = false) {
      const imageAnimated = entry.image
        ? entry.image.classList.contains('animated')
        : false
      const linesAnimated = entry.lines.some((line) =>
        line.classList.contains('animated')
      )
      const isAnimated = imageAnimated || linesAnimated

      if (shouldAnimate && !isAnimated) {
        if (entry.image) {
          entry.image.classList.add('animated')
        }
        entry.lines.forEach((line) => line.classList.add('animated'))
        this.animatedElements.add(entry)
      } else if (!shouldAnimate && isAnimated) {
        // 允许动画双向执行
        if (entry.image) {
          entry.image.classList.remove('animated')
        }
        entry.lines.forEach((line) => line.classList.remove('animated'))
        this.animatedElements.delete(entry)
      }
    }

    updateTrailingHeight() {
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight || 1
      const contentHeight = this.content
        ? this.content.getBoundingClientRect().height
        : 0

      // 根据单元数量动态计算需要的滚动距离，保证能高亮到最后一个元素
      // 减少单位距离，让动画更快完成
      const unitDistance = Math.max(1, this.totalUnits) * 10 // 40px per unit
      const highlightScrollDistanceBase = Math.max(
        unitDistance,
        viewportHeight * 1.2
      )
      const highlightDelayPx = (this.getHighlightDelayVh() / 100) * viewportHeight
      const highlightScrollDistance =
        highlightDelayPx +
        highlightScrollDistanceBase * this.getHighlightScrollMultiplier()

      const trailingPx =
        contentHeight + viewportHeight + highlightScrollDistance
      const trailingVh = (trailingPx / viewportHeight) * 100

      this.style.setProperty(
        '--pta-trailing-height',
        `${Math.max(300, Math.ceil(trailingVh))}vh`
      )
    }

    bindEvents() {
      this.handleScroll = this.handleScroll.bind(this)
      this.handleResize = this.handleResize.bind(this)

      // 使用 requestAnimationFrame 优化滚动性能
      // 确保每次滚动都能快速响应，不跳过任何帧
      this.scrollHandler = () => {
        if (!this.isScrolling) {
          this.isScrolling = true
          this.rafId = requestAnimationFrame(() => {
            this.handleScroll()
            this.isScrolling = false
          })
        }
      }

      this.resizeHandler = this.throttle(this.handleResize, 200)

      window.addEventListener('scroll', this.scrollHandler, { passive: true })
      window.addEventListener('resize', this.resizeHandler)
    }

    /**
     * 计算上一个 section 的滚动进度
     * 返回 0-1 之间的值，表示上一个 section 已经滚动了多少
     */
    getPreviousSectionProgress() {
      if (!this.previousSection) {
        return 1
      }

      const rect = this.previousSection.getBoundingClientRect()
      const viewportHeight =
        window.innerHeight || document.documentElement.clientHeight

      // 如果上一个 section 还在视口下方，返回 0
      if (rect.top >= viewportHeight) {
        return 0
      }

      // 如果上一个 section 已经完全滚出视口，返回 1
      if (rect.bottom <= 0) {
        return 1
      }

      // 计算滚动进度
      const sectionHeight = Math.max(
        rect.height,
        this.previousSection.offsetHeight || 1
      )
      const scrolled = viewportHeight - rect.top
      const progress = scrolled / (sectionHeight + viewportHeight)

      return Math.max(0, Math.min(1, progress))
    }

    /**
     * 计算当前组件的滚动进度
     * 返回一个对象，包含 reveal 和 highlight 两个阶段的进度
     * 优化：缓存 rect 和 viewportHeight，减少重复计算
     */
    calculateScrollProgress() {
      // 缓存 viewportHeight，只在窗口大小改变时更新
      if (!this.cachedViewportHeight) {
        this.cachedViewportHeight =
          window.innerHeight || document.documentElement.clientHeight
      }
      const viewportHeight = this.cachedViewportHeight

      // 缓存 rect，但每次滚动时更新（因为位置会变）
      const rect = this.getBoundingClientRect()
      const sectionHeight = Math.max(rect.height, 1)

      // 整体进度
      const scrolledIntoView = viewportHeight - rect.top
      const totalScrollDistance = sectionHeight + viewportHeight
      const overallProgress = Math.max(
        0,
        Math.min(1, scrolledIntoView / totalScrollDistance)
      )

      // Reveal 阶段：当组件底部进入视口底部时开始（rect.bottom <= viewportHeight）
      // 持续一个视口高度的滚动距离
      let revealProgress = 0

      // 如果 reveal 已经完成，直接返回 1
      if (this.revealCompleted) {
        revealProgress = 1
      } else {
        if (rect.bottom <= viewportHeight && rect.bottom > 0) {
          // 组件底部已经进入视口
          const scrolledSinceEnter = viewportHeight - rect.bottom
          const revealDistance = viewportHeight * 0.6 // reveal阶段持续0.6个视口高度
          revealProgress = Math.min(1, scrolledSinceEnter / revealDistance)
        } else if (rect.bottom <= 0 || rect.top < viewportHeight * 0.4) {
          // 组件底部已经完全进入视口，或者组件顶部距离视口顶部不到40%
          revealProgress = 1
        }
      }

      // Highlight 阶段：reveal 完成后开始
      // 优化：使用更直接的滚动距离计算，让动画更快响应滚动
      let highlightProgressRaw = 0
      if (revealProgress >= 0.95) {
        // 当 reveal 接近完成时开始高亮
        // 使用更小的单位距离，让动画更快完成，响应更快
        const highlightDelayPx = (this.getHighlightDelayVh() / 100) * viewportHeight
        const effectiveTop = Math.max(0, -rect.top - highlightDelayPx)
        // 减少每个单元的距离，让动画更快响应滚动
        // 根据滚动速度动态调整：快速滚动时减少距离，让动画更快
        const unitDistance = Math.max(1, this.totalUnits) * 40
        // 关键：高亮必须在本 section 结束前“走完”
        // 最大有效滚动位移（考虑延迟后的剩余范围）
        const availableScroll = Math.max(1, sectionHeight - viewportHeight - highlightDelayPx)
        const highlightDistance = Math.max(
          availableScroll,
          unitDistance,
          viewportHeight * 0.8 // 从 1.0 减少到 0.8，让动画更快完成
        )
        highlightProgressRaw = Math.min(1, effectiveTop / highlightDistance)
      }

      return {
        overall: overallProgress,
        reveal: revealProgress,
        highlight: highlightProgressRaw,
        highlightRaw: highlightProgressRaw, // 保存真实进度用于检测滚动方向
      }
    }

    handleScroll() {
      const scrollProgress = this.calculateScrollProgress()

      // 直接使用当前组件的滚动进度，不再依赖上一个section
      this.revealProgress = scrollProgress.reveal

      // 使用真实进度检测是否需要解锁
      const rawProgress = scrollProgress.highlightRaw

      // 检测 highlight 动画是否在反向执行（向上滚动）
      // 只有当真实进度显著降低时才解锁（说明是向上滚动）
      if (this.highlightCompleted && rawProgress < 0.95) {
        // 当向上滚动导致进度降低时，取消完成锁定，允许反向执行
        this.highlightCompleted = false
      }

      // 如果已完成，使用锁定值 1；否则使用真实进度
      this.highlightProgress = this.highlightCompleted ? 1 : rawProgress

      // 更新 reveal 效果
      this.updateReveal(this.revealProgress)

      // 更新 highlight 效果
      if (this.revealProgress >= 1) {
        this.updateHighlight(this.highlightProgress)
      } else {
        this.updateHighlight(0)
      }

      // 更新进度条
      this.updateProgress(scrollProgress.overall)
    }

    /**
     * 更新 reveal 效果 - 从底部逐渐显示内容
     */
    updateReveal(progress) {
      if (!this.stage) {
        return
      }

      // 如果 reveal 已经完成，不再重复执行 clip-path 动画
      if (this.revealCompleted) {
        return
      }

      // 使用 clip-path 实现从底部显示的效果
      // inset(top right bottom left)
      // 从底部显示：控制 bottom 值，从 100% 到 0%
      const clipProgress = progress * 100
      this.stage.style.clipPath = `inset(0% 0% ${100 - clipProgress}% 0%)`

      // 透明度渐变
      this.stage.style.opacity = Math.max(0, Math.min(1, progress * 1.5))

      // 当 reveal 完成时，标记为已完成
      if (progress >= 1) {
        this.revealCompleted = true
        // 确保最终状态正确应用
        this.stage.style.clipPath = 'inset(0% 0% 0% 0%)'
        this.stage.style.opacity = '1'
      }
    }

    /**
     * 更新 highlight 效果 - 逐个高亮文字和图片
     * 优化：使用更精确的计算，让动画更快响应滚动
     */
    updateHighlight(progress) {
      const totalUnits = Math.max(this.totalUnits, 1)
      // 使用更精确的计算，不依赖 Math.ceil，让动画更流畅
      const targetUnits = Math.max(
        0,
        Math.min(totalUnits, Math.floor(progress * totalUnits + 0.5)) // 使用四舍五入而不是向上取整
      )

      // 检测动画是否已完成 (进度达到或接近100%)
      const isCompleted = progress >= 0.98 // 从 0.99 降低到 0.98，让完成检测更早触发

      this.updateAnimations(targetUnits, isCompleted)

      // 当 highlight 完成时，标记为已完成并锁定状态
      if (isCompleted && !this.highlightCompleted) {
        this.highlightCompleted = true
      }
    }

    updateAnimations(targetUnits, isCompleted = false) {
      let cursor = 0
      const totalUnits = Math.max(this.totalUnits, 1)

      // 计算所有文字单元的总数
      let totalWords = 0
      this.timeline.forEach((entry) => {
        if (entry.type === 'text') {
          totalWords += entry.words.length
        }
      })

      this.timeline.forEach((entry) => {
        switch (entry.type) {
          case 'text':
            entry.words.forEach((word) => {
              const shouldAnimate = cursor < targetUnits || isCompleted
              this.toggleWordAnimation(word, shouldAnimate, isCompleted)
              cursor += 1
            })
            break

          case 'image':
            if (entry.element) {
              // 每个图片在轮到它的时候单独动画
              const shouldAnimate = cursor < targetUnits || isCompleted
              // 计算当前图片的动画进度（0-1）
              const imageStartUnit = cursor
              const imageEndUnit = cursor + entry.unitCount
              let imageProgress = 0

              if (isCompleted || targetUnits >= imageEndUnit) {
                // 完全完成
                imageProgress = 1
              } else if (targetUnits > imageStartUnit) {
                // 进行中
                imageProgress = (targetUnits - imageStartUnit) / entry.unitCount
              }

              this.toggleImageAnimation(
                entry.element,
                shouldAnimate,
                imageProgress,
                isCompleted
              )
            }
            cursor += entry.unitCount
            break

          case 'logo':
            {
              const shouldAnimate = cursor < targetUnits || isCompleted
              this.toggleLogoTextAnimation(entry, shouldAnimate, isCompleted)
              cursor += entry.unitCount
            }
            break
          case 'center': {
            const shouldAnimate = cursor < targetUnits || isCompleted
            this.toggleCenterBlockAnimation(entry, shouldAnimate, isCompleted)
            cursor += entry.unitCount
            break
          }

          default:
            cursor += entry.unitCount
            break
        }
      })
    }

    toggleWordAnimation(wordElement, shouldAnimate, isCompleted = false) {
      const isAnimated = wordElement.classList.contains('animated')

      if (shouldAnimate && !isAnimated) {
        wordElement.classList.add('animated')
        this.animatedElements.add(wordElement)
      } else if (!shouldAnimate && isAnimated) {
        // 允许动画双向执行
        wordElement.classList.remove('animated')
        this.animatedElements.delete(wordElement)
      }
    }

    toggleImageAnimation(
      imageElement,
      shouldAnimate,
      progress = 1,
      isCompleted = false
    ) {
      const wrapper = imageElement.closest(
        '.progressive-text-animation__image-wrapper'
      )
      const isAnimated = imageElement.classList.contains('animated')

      if (shouldAnimate && !isAnimated) {
        imageElement.classList.add('animated')
        if (wrapper) {
          wrapper.classList.add('animated')
        }
        this.animatedElements.add(imageElement)
      } else if (!shouldAnimate && isAnimated) {
        // 允许动画双向执行
        imageElement.classList.remove('animated')
        if (wrapper) {
          wrapper.classList.remove('animated')
        }
        this.animatedElements.delete(imageElement)
      }

      // 根据滚动进度动态调整缩放比例和透明度
      // 优化：使用 CSS transform 和 will-change 提升性能
      if (wrapper) {
        if (shouldAnimate) {
          // 渐进式放大：从 scale(0.5) 到 scale(1)
          // 直接使用进度，不使用 easing，让动画更快响应滚动
          const scaleProgress = Math.min(1, progress)
          const scale = 0.5 + 0.5 * scaleProgress // 0.5 -> 1.0
          const opacity = 0.3 + 0.7 * scaleProgress // 0.3 -> 1.0

          // 使用 transform 而不是直接修改 style，性能更好
          wrapper.style.transform = `scale(${scale})`
          wrapper.style.opacity = opacity
          wrapper.style.willChange = 'transform, opacity'
        } else {
          // 恢复初始状态
          wrapper.style.transform = 'scale(0.7)'
          wrapper.style.opacity = '0.3'
          wrapper.style.willChange = 'auto'
        }
      }
    }

    toggleLogoTextAnimation(entry, shouldAnimate, isCompleted = false) {
      const logoAnimated = entry.logo
        ? entry.logo.classList.contains('animated')
        : false
      const wordsAnimated = entry.words.some((word) =>
        word.classList.contains('animated')
      )
      const isAnimated = logoAnimated || wordsAnimated

      if (shouldAnimate && !isAnimated) {
        if (entry.logo) {
          entry.logo.classList.add('animated')
        }
        entry.words.forEach((word) => word.classList.add('animated'))
        this.animatedElements.add(entry)
      } else if (!shouldAnimate && isAnimated) {
        // 允许动画双向执行
        if (entry.logo) {
          entry.logo.classList.remove('animated')
        }
        entry.words.forEach((word) => word.classList.remove('animated'))
        this.animatedElements.delete(entry)
      }
    }

    updateProgress(progressRatio) {
      if (this.progressBar) {
        const clamped = Math.max(0, Math.min(1, progressRatio))
        this.progressBar.style.width = `${clamped * 100}%`
      }
    }

    handleResize() {
      // 清除缓存
      this.cachedRect = null
      this.cachedViewportHeight = null
      this.setupElements()
      this.handleScroll()
    }

    resetAnimations() {
      this.words.forEach((word) => word.classList.remove('animated'))
      this.images.forEach((image) => {
        image.classList.remove('animated')
        const wrapper = image.closest(
          '.progressive-text-animation__image-wrapper'
        )
        if (wrapper) {
          wrapper.classList.remove('animated')
        }
      })
      this.logoTextBlocks.forEach((entry) => {
        if (entry.logo) {
          entry.logo.classList.remove('animated')
        }
        entry.words.forEach((word) => word.classList.remove('animated'))
      })
      this.centerBlocks.forEach((entry) => {
        if (entry.image) {
          entry.image.classList.remove('animated')
        }
        entry.lines.forEach((line) => line.classList.remove('animated'))
      })
      this.animatedElements.clear()

      if (this.progressBar) {
        this.progressBar.style.width = '0%'
      }

      // 重置 reveal 效果
      if (this.stage) {
        this.stage.style.clipPath = 'inset(0% 0% 100% 0%)'
        this.stage.style.opacity = '0'
      }

      // 重置完成标志
      this.revealCompleted = false
      this.highlightCompleted = false
    }

    throttle(fn, limit) {
      let inThrottle = false
      return (...args) => {
        if (inThrottle) {
          return
        }
        inThrottle = true
        fn.apply(this, args)
        setTimeout(() => {
          inThrottle = false
        }, limit)
      }
    }

    findPreviousSection() {
      if (!this.wrapper) {
        return null
      }

      let sibling = this.wrapper.previousElementSibling
      while (sibling) {
        if (
          sibling.classList &&
          sibling.classList.contains('shopify-section')
        ) {
          return sibling
        }
        sibling = sibling.previousElementSibling
      }

      return null
    }

    disconnectedCallback() {
      // 取消 RAF
      if (this.rafId !== null) {
        cancelAnimationFrame(this.rafId)
        this.rafId = null
      }
      
      if (this.scrollHandler) {
        window.removeEventListener('scroll', this.scrollHandler)
      }
      if (this.resizeHandler) {
        window.removeEventListener('resize', this.resizeHandler)
      }
    }
  }

  customElements.define(
    'progressive-text-animation-component',
    ProgressiveTextAnimationComponent
  )

  window.addEventListener('pageshow', (event) => {
    if (event.persisted) {
      document
        .querySelectorAll('progressive-text-animation-component')
        .forEach((component) => {
          if (component.handleScroll) {
            component.handleScroll()
          }
        })
    }
  })
})()
